import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PhoneIcon from "@mui/icons-material/Phone";
import SchoolIcon from "@mui/icons-material/School";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useLocation, useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import PageLayout from "examples/LayoutContainers/PageLayout";
import Footer from "layouts/authentication/components/Footer";

import bannerLogo from "assets/FirstPageLogo.jpg";
import FirstpageLogo from "assets/images/Loginimage.png";
import FirstpageLogo1 from "assets/images/LoginPageImage.jpg";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS } from "config/api";
import { decryptText, encryptText } from "config/encryption";
import axiosInstance from "API/axiosInstance";

function CheckingAuth() {
  const [rollNo, setRollNo] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState("roll");
  const [userType, setUserType] = useState("student");

  const location = useLocation();
  const navigate = useNavigate();

  const decryptResponse = (encryptedText) => {
    try {
      return decryptText(encryptedText);
    } catch (err) {
      // console.error("Decryption error:", err);
      return null;
    }
  };

  useEffect(() => {
    const msg = location.state?.message;
    if (msg) setError(msg);

    const sessionData = localStorage.getItem("userSession");
    const sessionLoggedUserDetail = localStorage.getItem("UserLogDetails");

    if (sessionData && sessionLoggedUserDetail) {
      const decryptedUserDetails = decryptText(sessionData);
      const parsedUserDetails = JSON.parse(decryptedUserDetails);
      const usn = parsedUserDetails?.usn || "";

      const decryptedLoggedUserDetails = decryptText(sessionLoggedUserDetail);
      const parsedLoggedUserDetails = JSON.parse(decryptedLoggedUserDetails);
      const lus = parsedLoggedUserDetails?.UserName || "";

      if (usn === lus) {
        safeToast.warn("Already found Login. Redirecting to Myprofile...");
        setTimeout(() => {
          navigate("/MyDetails");
        }, 1000);
      }
    }
  }, [location.state]);

  const handleCheck = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    const input = loginMode === "roll" ? rollNo.trim().toUpperCase() : mobileNo.trim();
    if (!input) {
      setError(`Please enter a valid ${loginMode === "roll" ? (userType === "student" ? "Roll Number" : "Login Code") : "Mobile Number"}.`);
      setLoading(false);
      return; 
    }

    // Show under development for Admin Mobile Number
    if (userType === "staff" && loginMode === "mobile") {
      setError("This feature is under development.");
      setLoading(false);
      return;
    }

    if (loginMode === "mobile" && input.length !== 10) {
      setError("Please enter a valid Mobile Number.");
      setLoading(false);
      return;
    }

    try {
      let encrypted, decrypted;

      if (userType === "staff" && loginMode === "roll") {
        // Admin login with Login Code
        const encryptedLoginCode = encryptText(input);
        const { data } = await axiosInstance.post(
          API_ENDPOINTS.AUTH.CHECK_STAFF_BY_LOGIN_CODE,
          { loginCode: encryptedLoginCode }
        );
        decrypted = decryptResponse(data);

        if (decrypted) {
          localStorage.removeItem("adminsessiondetails");
          localStorage.setItem("adminsessiondetails", data);
          setMessage("Admin found. Redirecting...");
          setTimeout(() => {
            navigate("/AdminLogin");
          }, 2000);
        } else {
          setError("Unable to decrypt admin response.");
        }
      } else {
        // Student or other login
        const { data: encrypted } = await axiosInstance.post(
          API_ENDPOINTS.AUTH.CHECK_USER,
          loginMode === "roll" ? { rollNum: input } : { mobile: input }
        );
        decrypted = decryptResponse(encrypted);

        if (decrypted) {
          localStorage.removeItem("userSession");
          localStorage.removeItem("UserLogDetails");
          localStorage.setItem("userSession", encrypted);
          setMessage("User found. Redirecting...");
          setTimeout(() => {
            navigate("/authentication/sign-in");
          }, 2000);
        } else {
          setError("Unable to decrypt response.");
        }
      }
    } catch (err) {
      // console.error("CheckUser error:", err);
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRollNo("");
    setMobileNo("");
    setMessage("");
    setError("");
  };

  return (
    <PageLayout>
      <MDBox
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        sx={{
          background: (theme) => 
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${theme.palette.primary.dark}20 0%, ${theme.palette.info.dark}20 50%, ${theme.palette.secondary.dark}20 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.light}08 0%, ${theme.palette.info.light}08 50%, ${theme.palette.secondary.light}08 100%)`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: (theme) => 
              theme.palette.mode === 'dark'
                ? `radial-gradient(circle at 20% 50%, ${theme.palette.primary.dark}15 0%, transparent 50%),
                   radial-gradient(circle at 80% 80%, ${theme.palette.info.dark}15 0%, transparent 50%)`
                : `radial-gradient(circle at 20% 50%, ${theme.palette.primary.light}12 0%, transparent 50%),
                   radial-gradient(circle at 80% 80%, ${theme.palette.info.light}12 0%, transparent 50%)`,
            pointerEvents: "none",
            animation: "pulse 8s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.8 },
            },
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-50%",
            right: "-50%",
            width: "200%",
            height: "200%",
            background: (theme) => 
              `radial-gradient(circle, ${theme.palette.primary.main}05 0%, transparent 70%)`,
            animation: "rotate 20s linear infinite",
            "@keyframes rotate": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
          },
        }}
      >
        <MDBox 
          flexGrow={1} 
          px={{ xs: 1.5, sm: 2 }} 
          py={{ xs: 2, sm: 3 }} 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          position="relative" 
          zIndex={1}
        >
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6} lg={5}>
              <Fade in timeout={800}>
                <Card
                  elevation={0}
                  sx={{
                    background: (theme) => 
                      theme.palette.mode === 'dark'
                        ? `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
                        : theme.palette.background.paper,
                    borderRadius: { xs: 2, sm: 3 },
                    boxShadow: (theme) => 
                      theme.palette.mode === 'dark'
                        ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px ${theme.palette.divider}`
                        : `0 12px 40px rgba(0,0,0,0.06), 0 0 0 1px ${theme.palette.divider}`,
                    p: { xs: 2, sm: 2.5, md: 3 },
                    mx: "auto",
                    maxWidth: { xs: "100%", sm: 480 },
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: (theme) => 
                        `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.info.main}, ${theme.palette.secondary.main})`,
                      backgroundSize: "200% 100%",
                      animation: "shimmer 3s ease-in-out infinite",
                      "@keyframes shimmer": {
                        "0%": { backgroundPosition: "-200% 0" },
                        "100%": { backgroundPosition: "200% 0" },
                      },
                    },
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: (theme) => 
                        theme.palette.mode === 'dark'
                          ? `0 24px 72px rgba(0,0,0,0.6), 0 0 0 1px ${theme.palette.divider}`
                          : `0 24px 72px rgba(0,0,0,0.12), 0 0 0 1px ${theme.palette.divider}`,
                    },
                  }}
                >
                  {/* Welcome Card Section */}
                  <Card
                    elevation={1}
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      minHeight: { xs: 90, sm: 100 },
                    }}
                  >
                    <MDBox display="flex" justifyContent="space-between" pt={{ xs: 1, sm: 0.75 }} px={{ xs: 1.5, sm: 1.5 }}>
                      <MDBox
                        sx={{
                          background: (theme) => 
                            `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                          color: "white",
                          boxShadow: (theme) => `0 2px 8px ${theme.palette.info.main}25`,
                          borderRadius: (theme) => theme.borders.borderRadius.md,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: { xs: "2.5rem", sm: "2.75rem" },
                          height: { xs: "2.5rem", sm: "2.75rem" },
                          mt: -2,
                        }}
                      >
                        <Icon 
                          sx={{ fontSize: { xs: 18, sm: 20 }, color: "white !important" }} 
                        >
                          school
                        </Icon>
                      </MDBox>
                      <MDBox textAlign="right" lineHeight={1.2} flex={1} pl={{ xs: 0.75, sm: 0 }}>
                        <MDTypography 
                          variant="button" 
                          fontWeight="light" 
                          color="text"
                          sx={{ fontSize: { xs: 9, sm: 10 } }}
                        >
                          Welcome to JJCET
                        </MDTypography>
                        <MDTypography 
                          variant="h4"
                          sx={{ 
                            fontSize: { xs: 14, sm: 16, md: 18 },
                            fontWeight: 600,
                          }}
                        >
                          Alumni Portal
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <Divider />
                    <MDBox pb={{ xs: 1.5, sm: 1.25 }} px={{ xs: 1.5, sm: 1.5 }}>
                      <MDTypography 
                        component="p" 
                        variant="button" 
                        color="text" 
                        display="flex"
                        sx={{ 
                          fontSize: { xs: 10, sm: 10 },
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        Select login type and verify your identity
                      </MDTypography>
                    </MDBox>
                  </Card>

                  {/* User Type Selection */}
                  <MDBox mb={1.5}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      color="text"
                      sx={{ 
                        mb: 1,
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        fontSize: { xs: 9, sm: 10 },
                        opacity: 0.7,
                      }}
                    >
                      I am a
                    </MDTypography>
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <MDBox
                          onClick={() => { 
                            setUserType("student"); 
                            setLoginMode("roll");
                            setError("");
                            setMessage("");
                          }}
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            transition: "transform 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                            },
                          }}
                        >
                          <Card
                            elevation={userType === "student" ? 3 : 1}
                            sx={{
                              height: "100%",
                              minHeight: { xs: 75, sm: 85 },
                              borderRadius: 2,
                              border: (theme) => 
                                userType === "student" 
                                  ? `2px solid ${theme.palette.primary.main}` 
                                  : `1px solid ${theme.palette.divider}`,
                              transition: "all 0.3s ease",
                            }}
                          >
                            <MDBox display="flex" justifyContent="space-between" pt={{ xs: 0.75, sm: 0.5 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDBox
                                sx={{
                                  background: (theme) => 
                                    userType === "student"
                                      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                      : `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                                  color: "white",
                                  boxShadow: (theme) => 
                                    userType === "student"
                                      ? `0 2px 6px ${theme.palette.primary.main}20`
                                      : `0 2px 6px ${theme.palette.secondary.main}20`,
                                  borderRadius: (theme) => theme.borders.borderRadius.md,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: { xs: "2rem", sm: "2.25rem" },
                                  height: { xs: "2rem", sm: "2.25rem" },
                                  mt: -1.75,
                                }}
                              >
                                <Icon 
                                  sx={{ fontSize: { xs: 16, sm: 18 }, color: (theme) => theme.palette.common.white }} 
                                >
                                  person
                                </Icon>
                              </MDBox>
                              <MDBox textAlign="right" lineHeight={1.15} flex={1} pl={{ xs: 0.5, sm: 0 }}>
                                <MDTypography 
                                  variant="button" 
                                  fontWeight="light" 
                                  color="text"
                                  sx={{ fontSize: { xs: 8, sm: 8, md: 9 } }}
                                >
                                  I am a
                                </MDTypography>
                                <MDTypography 
                                  variant="h4"
                                  sx={{ 
                                    fontSize: { xs: 12, sm: 14, md: 16 },
                                    fontWeight: 600,
                                  }}
                                >
                                  Alumni
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <Divider />
                            <MDBox pb={{ xs: 1.25, sm: 1 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDTypography 
                                component="p" 
                                variant="button" 
                                color="text" 
                                display="flex"
                                sx={{ 
                                  fontSize: { xs: 9, sm: 9 },
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {userType === "student" ? "Selected" : "Click to select"}
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDBox
                          onClick={() => { 
                            setUserType("staff"); 
                            setLoginMode("roll");
                            setError("");
                            setMessage("");
                          }}
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            transition: "transform 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                            },
                          }}
                        >
                          <Card
                            elevation={userType === "staff" ? 3 : 1}
                            sx={{
                              height: "100%",
                              minHeight: { xs: 75, sm: 85 },
                              borderRadius: 2,
                              border: (theme) => 
                                userType === "staff" 
                                  ? `2px solid ${theme.palette.info.main}` 
                                  : `1px solid ${theme.palette.divider}`,
                              transition: "all 0.3s ease",
                            }}
                          >
                            <MDBox display="flex" justifyContent="space-between" pt={{ xs: 0.75, sm: 0.5 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDBox
                                sx={{
                                  background: (theme) => 
                                    userType === "staff"
                                      ? `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`
                                      : `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                                  color: "white",
                                  boxShadow: (theme) => 
                                    userType === "staff"
                                      ? `0 2px 6px ${theme.palette.info.main}20`
                                      : `0 2px 6px ${theme.palette.secondary.main}20`,
                                  borderRadius: (theme) => theme.borders.borderRadius.md,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: { xs: "2rem", sm: "2.25rem" },
                                  height: { xs: "2rem", sm: "2.25rem" },
                                  mt: -1.75,
                                }}
                              >
                                <Icon 
                                  sx={{ fontSize: { xs: 16, sm: 18 }, color: (theme) => theme.palette.common.white }} 
                                >
                                  admin_panel_settings
                                </Icon>
                              </MDBox>
                              <MDBox textAlign="right" lineHeight={1.15} flex={1} pl={{ xs: 0.5, sm: 0 }}>
                                <MDTypography 
                                  variant="button" 
                                  fontWeight="light" 
                                  color="text"
                                  sx={{ fontSize: { xs: 8, sm: 8, md: 9 } }}
                                >
                                  I am a
                                </MDTypography>
                                <MDTypography 
                                  variant="h4"
                                  sx={{ 
                                    fontSize: { xs: 12, sm: 14, md: 16 },
                                    fontWeight: 600,
                                  }}
                                >
                                  Admin
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <Divider />
                            <MDBox pb={{ xs: 1.25, sm: 1 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDTypography 
                                component="p" 
                                variant="button" 
                                color="text" 
                                display="flex"
                                sx={{ 
                                  fontSize: { xs: 9, sm: 9 },
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {userType === "staff" ? "Selected" : "Click to select"}
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Login Mode Selection */}
                  <MDBox mb={1.5}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      color="text"
                      sx={{ 
                        mb: 1,
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        fontSize: { xs: 9, sm: 10 },
                        opacity: 0.7,
                      }}
                    >
                      Verification Method
                    </MDTypography>
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <MDBox
                          onClick={() => {
                            setLoginMode("roll");
                            setError("");
                            setMessage("");
                          }}
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            transition: "transform 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                            },
                          }}
                        >
                          <Card
                            elevation={loginMode === "roll" ? 3 : 1}
                            sx={{
                              height: "100%",
                              minHeight: { xs: 75, sm: 85 },
                              borderRadius: 2,
                              border: (theme) => 
                                loginMode === "roll" 
                                  ? `2px solid ${userType === "student" ? theme.palette.warning.main : theme.palette.info.main}` 
                                  : `1px solid ${theme.palette.divider}`,
                              transition: "all 0.3s ease",
                            }}
                          >
                            <MDBox display="flex" justifyContent="space-between" pt={{ xs: 0.75, sm: 0.5 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDBox
                                sx={{
                                  background: (theme) => {
                                    if (loginMode === "roll") {
                                      return userType === "student"
                                        ? `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                                        : `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`;
                                    }
                                    return `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`;
                                  },
                                  color: "white",
                                  boxShadow: (theme) => {
                                    if (loginMode === "roll") {
                                      return userType === "student"
                                        ? `0 2px 6px ${theme.palette.warning.main}20`
                                        : `0 2px 6px ${theme.palette.info.main}20`;
                                    }
                                    return `0 2px 6px ${theme.palette.secondary.main}20`;
                                  },
                                  borderRadius: (theme) => theme.borders.borderRadius.md,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: { xs: "2rem", sm: "2.25rem" },
                                  height: { xs: "2rem", sm: "2.25rem" },
                                  mt: -1.75,
                                }}
                              >
                                <Icon 
                                  sx={{ fontSize: { xs: 16, sm: 18 }, color: (theme) => theme.palette.common.white }} 
                                >
                                  vpn_key
                                </Icon>
                              </MDBox>
                              <MDBox textAlign="right" lineHeight={1.15} flex={1} pl={{ xs: 0.5, sm: 0 }}>
                                <MDTypography 
                                  variant="button" 
                                  fontWeight="light" 
                                  color="text"
                                  sx={{ fontSize: { xs: 8, sm: 8, md: 9 } }}
                                >
                                  Verification
                                </MDTypography>
                                <MDTypography 
                                  variant="h4"
                                  sx={{ 
                                    fontSize: { xs: 11, sm: 13, md: 15 },
                                    fontWeight: 600,
                                  }}
                                >
                                  {userType === "student" ? "Alumni Code" : "Login Code"}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <Divider />
                            <MDBox pb={{ xs: 1.25, sm: 1 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDTypography 
                                component="p" 
                                variant="button" 
                                color="text" 
                                display="flex"
                                sx={{ 
                                  fontSize: { xs: 9, sm: 9 },
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {loginMode === "roll" ? "Selected" : "Click to select"}
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDBox
                          onClick={() => {
                            if (userType !== "staff") {
                              setLoginMode("mobile");
                              setError("");
                              setMessage("");
                            }
                          }}
                          sx={{
                            cursor: userType === "staff" ? "not-allowed" : "pointer",
                            height: "100%",
                            transition: "transform 0.2s ease",
                            opacity: userType === "staff" ? 0.5 : 1,
                            "&:hover": {
                              transform: userType === "staff" ? "none" : "translateY(-4px)",
                            },
                          }}
                        >
                          <Card
                            elevation={loginMode === "mobile" ? 3 : 1}
                            sx={{
                              height: "100%",
                              minHeight: { xs: 75, sm: 85 },
                              borderRadius: 2,
                              border: (theme) => 
                                loginMode === "mobile" 
                                  ? `2px solid ${theme.palette.warning.main}` 
                                  : `1px solid ${theme.palette.divider}`,
                              transition: "all 0.3s ease",
                            }}
                          >
                            <MDBox display="flex" justifyContent="space-between" pt={{ xs: 0.75, sm: 0.5 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDBox
                                sx={{
                                  background: (theme) => 
                                    loginMode === "mobile"
                                      ? `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                                      : `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                                  color: "white",
                                  boxShadow: (theme) => 
                                    loginMode === "mobile"
                                      ? `0 2px 6px ${theme.palette.warning.main}20`
                                      : `0 2px 6px ${theme.palette.secondary.main}20`,
                                  borderRadius: (theme) => theme.borders.borderRadius.md,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: { xs: "2rem", sm: "2.25rem" },
                                  height: { xs: "2rem", sm: "2.25rem" },
                                  mt: -1.75,
                                }}
                              >
                                <Icon 
                                  sx={{ fontSize: { xs: 16, sm: 18 }, color: (theme) => theme.palette.common.white }} 
                                >
                                  phone
                                </Icon>
                              </MDBox>
                              <MDBox textAlign="right" lineHeight={1.15} flex={1} pl={{ xs: 0.5, sm: 0 }}>
                                <MDTypography 
                                  variant="button" 
                                  fontWeight="light" 
                                  color="text"
                                  sx={{ fontSize: { xs: 8, sm: 8, md: 9 } }}
                                >
                                  Verification
                                </MDTypography>
                                <MDTypography 
                                  variant="h4"
                                  sx={{ 
                                    fontSize: { xs: 11, sm: 13, md: 15 },
                                    fontWeight: 600,
                                  }}
                                >
                                  Mobile
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <Divider />
                            <MDBox pb={{ xs: 1.25, sm: 1 }} px={{ xs: 1.25, sm: 1.25 }}>
                              <MDTypography 
                                component="p" 
                                variant="button" 
                                color="text" 
                                display="flex"
                                sx={{ 
                                  fontSize: { xs: 9, sm: 9 },
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {userType === "staff" 
                                  ? "Not available" 
                                  : loginMode === "mobile" 
                                    ? "Selected" 
                                    : "Click to select"}
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Input Section */}
                  <MDBox mb={2}>
                    <MDTypography 
                      variant="body2" 
                      color="text" 
                      mb={1.5} 
                      textAlign="left" 
                      sx={{ 
                        fontSize: { xs: 11, sm: 12 },
                        opacity: 0.8,
                        fontWeight: 500,
                      }}
                    >
                      {loginMode === "roll"
                        ? (userType === "student" 
                            ? "Enter your Alumni Code" 
                            : "Enter your Login Code")
                        : "Enter your mobile number"}
                    </MDTypography>

                    <Slide direction="down" in={loginMode === "roll"} timeout={300}>
                      <Box>
                        {loginMode === "roll" ? (
                          <MDInput
                            fullWidth
                            label={userType === "student" ? "Alumni Code" : "Login Code"}
                            placeholder={userType === "student" ? "e.g., ERPS001" : "e.g., ADMIN001"}
                            value={rollNo}
                            onChange={(e) => setRollNo(e.target.value.toUpperCase())}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <VpnKeyIcon sx={{ fontSize: { xs: 16, sm: 18 }, opacity: 0.6 }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                background: (theme) => 
                                  theme.palette.mode === 'dark' 
                                    ? theme.palette.background.default 
                                    : theme.palette.grey[50],
                                fontSize: { xs: 13, sm: 14 },
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: (theme) => 
                                    theme.palette.mode === 'dark' 
                                      ? theme.palette.background.default 
                                      : theme.palette.grey[100],
                                },
                                "&.Mui-focused": {
                                  background: (theme) => theme.palette.background.paper,
                                  boxShadow: (theme) => 
                                    `0 0 0 2px ${theme.palette.primary.main}20`,
                                },
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                    </Slide>

                    <Slide direction="down" in={loginMode === "mobile"} timeout={300}>
                      <Box>
                        {loginMode === "mobile" ? (
                          <MDInput
                            fullWidth
                            label="Mobile Number"
                            placeholder="e.g., 9876543210"
                            value={mobileNo}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                              setMobileNo(value);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon sx={{ fontSize: { xs: 16, sm: 18 }, opacity: 0.6 }} />
                                </InputAdornment>
                              ),
                            }}
                            type="tel"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                background: (theme) => 
                                  theme.palette.mode === 'dark' 
                                    ? theme.palette.background.default 
                                    : theme.palette.grey[50],
                                fontSize: { xs: 13, sm: 14 },
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: (theme) => 
                                    theme.palette.mode === 'dark' 
                                      ? theme.palette.background.default 
                                      : theme.palette.grey[100],
                                },
                                "&.Mui-focused": {
                                  background: (theme) => theme.palette.background.paper,
                                  boxShadow: (theme) => 
                                    `0 0 0 2px ${theme.palette.primary.main}20`,
                                },
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                    </Slide>
                  </MDBox>

                  {/* Alerts */}
                  <Fade in={!!error}>
                    <Box>
                      {error && (
                        <MDAlert 
                          color="error" 
                          dismissible 
                          onClose={() => setError("")}
                          sx={{ 
                            mb: 1.5, 
                            borderRadius: 2,
                            boxShadow: 1,
                            fontSize: { xs: 11, sm: 12 },
                            py: { xs: 0.75, sm: 1 },
                            "& .MuiAlert-icon": {
                              color: "error.main",
                              fontSize: { xs: 18, sm: 20 },
                            },
                          }}
                        >
                          {error}
                        </MDAlert>
                      )}
                    </Box>
                  </Fade>
                  <Fade in={!!message}>
                    <Box>
                      {message && (
                        <MDAlert 
                          color="success" 
                          dismissible 
                          onClose={() => setMessage("")}
                          sx={{ 
                            mb: 1.5,
                            borderRadius: 2,
                            boxShadow: 1,
                            fontSize: { xs: 11, sm: 12 },
                            py: { xs: 0.75, sm: 1 },
                            "& .MuiAlert-icon": {
                              color: "success.main",
                              fontSize: { xs: 18, sm: 20 },
                            },
                          }}
                        >
                          {message}
                        </MDAlert>
                      )}
                    </Box>
                  </Fade>
                  {userType === "staff" && loginMode === "mobile" && (
                    <MDAlert 
                      color="warning" 
                      sx={{ 
                        mb: 1.5,
                        borderRadius: 2,
                        boxShadow: 1,
                        fontSize: { xs: 11, sm: 12 },
                        py: { xs: 0.75, sm: 1 },
                        "& .MuiAlert-icon": {
                          color: "warning.main",
                          fontSize: { xs: 18, sm: 20 },
                        },
                      }}
                    >
                      This feature is under development.
                    </MDAlert>
                  )}

                  {/* Action Buttons */}
                  <Grid container spacing={1.5} mt={1.5}>
                    <Grid item xs={12} sm={6}>
                      <MDButton
                        fullWidth
                        color="primary"
                        onClick={handleCheck}
                        disabled={loading || (userType === "staff" && loginMode === "mobile")}
                        variant="gradient"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 600,
                          py: { xs: 1.25, sm: 1.5 },
                          fontSize: { xs: 13, sm: 14 },
                          textTransform: "none",
                          boxShadow: 3,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: 4,
                          },
                          "&:disabled": {
                            opacity: 0.6,
                          },
                        }}
                      >
                        {loading ? (
                          <MDBox display="flex" alignItems="center" gap={0.75}>
                            <CircularProgress size={16} color="inherit" />
                            <span>Verifying...</span>
                          </MDBox>
                        ) : (
                          "Verify & Continue"
                        )}
                      </MDButton>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDButton
                        fullWidth
                        color="secondary"
                        onClick={handleReset}
                        disabled={loading}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 600,
                          py: { xs: 1.25, sm: 1.5 },
                          fontSize: { xs: 13, sm: 14 },
                          textTransform: "none",
                          borderWidth: 1.5,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderWidth: 1.5,
                            boxShadow: 2,
                          },
                        }}
                      >
                        Clear
                      </MDButton>
                    </Grid>
                  </Grid>
                </Card>
              </Fade>
            </Grid>
            {/* Right Side Banner - Desktop Only */}
            <Grid 
              item 
              xs={12} 
              md={6} 
              lg={7}
              sx={{ 
                display: { xs: "none", md: "flex" }, 
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Slide direction="left" in timeout={1000}>
                <MDBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 600,
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: (theme) => 
                      `0 24px 60px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`,
                    border: (theme) => `2px solid ${theme.palette.divider}`,
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: (theme) => 
                        `0 32px 80px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.2)'}`,
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: (theme) => 
                        `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.info.main}10)`,
                      zIndex: 1,
                      pointerEvents: "none",
                    },
                  }}
                >
                  <MDBox
                    component="img"
                    src={FirstpageLogo1}
                    alt="IMPRES Alumni Portal"
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: 700,
                      objectFit: "cover",
                      display: "block",
                      borderRadius: 4,
                    }}
                  />
                </MDBox>
              </Slide>
            </Grid>
          </Grid>
        </MDBox>
{/* 
        <MDTypography
          variant="caption"
          display="block"
          textAlign="center"
          mt={5}
          color="text"
          sx={{ opacity: 0.7, fontSize: 16, letterSpacing: 0.5 }}
        >
          “Success doesn’t come from what you do occasionally, it comes from what you do
          consistently.” 💡
        </MDTypography> */}
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </MDBox>
    </PageLayout>
  );
}

export default CheckingAuth;
